import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import {
  Field,
  SectionCard,
  SectionDivider,
  StatRow,
  StatTile,
} from '@/components/ui/section-card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ChargeTimeline } from '@/components/charges/charge-timeline';
import { RefundModal } from '@/components/charges/refund-modal';
import { getApiErrorMessage } from '@/utils/api-error';
import { useToastStore } from '@/store/toastStore';
import { useAuthStore } from '@/store/authStore';
import { isProviderRole } from '@/utils/user-role';
import { Charge } from '@/types/charge';
import { CHARGE_STATUS } from '@/types/charge-status';
import { PROCESSING_METHOD } from '@/types/processing-method';
import { getEffectiveChargeStatus } from '@/utils/charge-status';
import { formatCurrency } from '@/utils/format-currency';
import { formatDate } from '@/utils/format-date';
import { paymentMethodLabel } from '@/constants/service-payment-methods';
import { CHARGE_ACTIONS, CHARGE_AMOUNTS, CHARGE_AUTOMATIC_BADGE } from '@/content/payments/charge';
import { cn } from '@/utils/cn';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useState } from 'react';
import {
  ActivityIndicator,
  Linking,
  ScrollView,
  View,
  useWindowDimensions,
  Pressable,
  Modal,
  Image,
  Clipboard,
} from 'react-native';
import { useConfirmPayment } from '@/hooks/use-confirm-payment';
import { useUnconfirmPayment } from '@/hooks/use-unconfirm-payment';
import { useConvertToAutomatic } from '@/hooks/use-convert-to-automatic';
import { useMembership } from '@/hooks/use-membership';

type ChargeDetailModalProps = {
  visible: boolean;
  charge: Charge | null;
  onClose: () => void;
  onUpdate?: () => void;
};

const getStatusDetails = (status: CHARGE_STATUS | undefined) => {
  if (!status) return { label: 'N/A', color: 'text-foreground', icon: 'help-circle-outline' };
  switch (status) {
    case CHARGE_STATUS.PAID:
      return { label: 'Pago', color: 'text-green-600', icon: 'checkmark-circle-outline' };
    case CHARGE_STATUS.PENDING:
      return { label: 'Pendente', color: 'text-orange-600', icon: 'time-outline' };
    case CHARGE_STATUS.OVERDUE:
      return { label: 'Vencido', color: 'text-red-600', icon: 'alert-circle-outline' };
    case CHARGE_STATUS.CANCELED:
      return { label: 'Cancelado', color: 'text-gray-500', icon: 'close-circle-outline' };
    case CHARGE_STATUS.PAYMENT_REPORTED:
      // Use primary color for 'Pagamento Informado' as requested (premium color)
      return {
        label: 'Pagamento Informado',
        color: 'text-primary',
        icon: 'information-circle-outline',
      };
    default:
      return { label: status, color: 'text-foreground', icon: 'help-circle-outline' };
  }
};

export function ChargeDetailModal({ visible, charge, onClose, onUpdate }: ChargeDetailModalProps) {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const { user } = useAuthStore();
  const isProvider = user ? isProviderRole(user.role) : false;
  const { can } = useMembership();

  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);
  const [isImageViewerVisible, setIsImageViewerVisible] = useState(false);
  const [isRefundOpen, setIsRefundOpen] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<'receipt' | 'convert' | null>(null);
  const { addToast } = useToastStore();

  const confirmPaymentMutation = useConfirmPayment();
  const unconfirmPaymentMutation = useUnconfirmPayment();
  const convertToAutomaticMutation = useConvertToAutomatic();

  if (!charge) return null;

  const statusDetails = getStatusDetails(getEffectiveChargeStatus(charge));
  const isPaid = charge.status === CHARGE_STATUS.PAID;
  const isReported = charge.status === CHARGE_STATUS.PAYMENT_REPORTED;
  const isAutomatic = charge.processingMethod === PROCESSING_METHOD.PLATFORM;
  const isOpenStatus =
    charge.status === CHARGE_STATUS.PENDING || charge.status === CHARGE_STATUS.OVERDUE;
  // Allow paying if pending, overdue, OR reported (to update proof)
  const canPay =
    (charge.status === CHARGE_STATUS.PENDING ||
      charge.status === CHARGE_STATUS.OVERDUE ||
      isReported) &&
    !isProvider &&
    !isPaid;
  // Prestador pode confirmar manualmente independente do status,
  // exceto se já estiver pago, cancelado ou reembolsado
  // No caixa único da equipe, a baixa fica com o dono: o dinheiro é da conta dele
  const openForConfirm =
    isProvider &&
    charge.status !== CHARGE_STATUS.PAID &&
    charge.status !== CHARGE_STATUS.CANCELED &&
    charge.status !== CHARGE_STATUS.REFUNDED &&
    charge.status !== CHARGE_STATUS.PARTIALLY_REFUNDED;
  const canSettle = can('settlePayments');
  // Um botão só para manual e automática: na automática a API cancela o
  // pagamento pendente e marca como recebida por fora; se o boleto já está
  // compensando, a própria API recusa com a mensagem certa.
  const canConfirm = openForConfirm && canSettle;
  const canUnconfirm =
    isPaid && isProvider && canSettle && charge.processingMethod === PROCESSING_METHOD.MANUAL;
  const canConvertToAutomatic =
    isProvider && canSettle && charge.processingMethod === PROCESSING_METHOD.MANUAL && isOpenStatus;
  const canRequestRefund =
    isProvider &&
    canSettle &&
    (charge.status === CHARGE_STATUS.PAID || charge.status === CHARGE_STATUS.PARTIALLY_REFUNDED);

  const handlePayPress = async () => {
    if (!charge) return;
    setIsLoadingCheckout(true);
    try {
      if (charge.paymentLink) {
        // Check if we should redirect to checkout screen first for manual charges or standard behavior
        // For now, let's keep the checkout redirection logic which handles uploading proofs
        router.push(`/checkout/${charge.id}`);
      } else {
        router.push(`/checkout/${charge.id}`);
      }
      addToast('Redirecionando para pagamento/anexo.', 'success');
      onClose();
    } catch (error: any) {
      console.error('Erro ao iniciar pagamento:', error);
      addToast('Ocorreu um erro ao iniciar o pagamento. Tente novamente.', 'error');
    } finally {
      setIsLoadingCheckout(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!charge) return;
    try {
      await confirmPaymentMutation.mutateAsync(charge.id);
      addToast(CHARGE_ACTIONS.confirmReceiptSuccess, 'success');
      onUpdate?.();
      onClose();
    } catch (error: any) {
      console.error('Erro ao confirmar recebimento:', error);
      addToast(getApiErrorMessage(error, CHARGE_ACTIONS.confirmReceiptError), 'error');
    }
  };

  const handleUnconfirmPayment = async () => {
    if (!charge) return;
    try {
      await unconfirmPaymentMutation.mutateAsync(charge.id);
      addToast('Confirmação de pagamento desfeita com sucesso!', 'success');
      onUpdate?.();
      onClose();
    } catch (error: any) {
      console.error('Erro ao desfazer confirmação de pagamento:', error);
      addToast(getApiErrorMessage(error, 'Erro ao desfazer confirmação de pagamento'), 'error');
    }
  };

  const handleConvertToAutomatic = async () => {
    if (!charge) return;
    try {
      await convertToAutomaticMutation.mutateAsync(charge.id);
      addToast('Cobrança convertida para automática.', 'success');
      onUpdate?.();
    } catch (error: any) {
      console.error('Erro ao converter para automático:', error);
      addToast(
        getApiErrorMessage(error, 'Não foi possível converter esta cobrança agora.'),
        'error',
      );
    }
  };

  const handleViewProof = async () => {
    if (charge.proofOfPaymentUrl) {
      const url = charge.proofOfPaymentUrl.toLowerCase();
      const isPdf = url.endsWith('.pdf');

      if (!isPdf) {
        setIsImageViewerVisible(true);
      } else {
        await Linking.openURL(charge.proofOfPaymentUrl);
      }
    }
  };

  const handleCopyPix = () => {
    if (charge.pixCopyPaste) {
      Clipboard.setString(charge.pixCopyPaste);
      addToast('Código Pix copiado!', 'success');
    }
  };

  const contentClassName = isMobile
    ? 'w-full h-full rounded-none'
    : 'w-full max-w-[500px] max-h-[90vh]';

  const modalBodyContent = (
    <>
      <Pressable
        onPress={onClose}
        className="absolute right-4 top-4 z-10 rounded-full p-2 hover:bg-neutral-100"
      >
        <Ionicons className="text-neutral-500" name="close" size={24} />
      </Pressable>

      <DialogHeader className="mb-4 pr-10">
        <DialogTitle>
          <ThemedText className="font-bold text-2xl">Detalhes da cobrança</ThemedText>
        </DialogTitle>
        <DialogDescription>
          <ThemedText className="text-sm text-foreground/40">
            Visualização completa dos dados desta cobrança.
          </ThemedText>
        </DialogDescription>
      </DialogHeader>

      <ScrollView
        keyboardShouldPersistTaps="handled"
        className={isMobile ? 'flex-1' : 'max-h-[65vh]'}
        showsVerticalScrollIndicator={false}
      >
        <View className="gap-6 pb-6 pr-2">
          {/* Status Hero Section */}
          <View
            className={cn(
              'flex-row items-center gap-4 rounded-2xl p-4',
              isPaid
                ? 'border border-green-100 bg-green-50'
                : isReported
                  ? 'border border-primary-100 bg-primary-50'
                  : 'border border-neutral-100 bg-neutral-50',
            )}
          >
            <View
              className={cn(
                'h-12 w-12 items-center justify-center rounded-full',
                statusDetails.color.replace('text-', 'bg-').concat('/10'),
              )}
            >
              <Ionicons
                name={statusDetails.icon as any}
                size={28}
                className={statusDetails.color}
              />
            </View>
            <View className="flex-1">
              <View className="flex-row flex-wrap items-center gap-2">
                <ThemedText className={cn('font-bold text-lg', statusDetails.color)}>
                  {statusDetails.label}
                </ThemedText>
                {isAutomatic && (
                  <View className="rounded-full bg-primary/10 px-2.5 py-0.5">
                    <ThemedText className="font-semibold text-xs text-primary">
                      {CHARGE_AUTOMATIC_BADGE}
                    </ThemedText>
                  </View>
                )}
              </View>
              {isPaid && (
                <ThemedText className="text-xs text-green-700">
                  Confirmado em {formatDate(charge.paidAt!)}
                </ThemedText>
              )}
              {isReported && !isPaid && (
                <ThemedText className="text-xs text-primary-700">
                  Aguardando {isProvider ? 'sua verificação' : 'confirmação do prestador'}
                </ThemedText>
              )}
            </View>
          </View>

          {/* Role-specific messages */}
          {isReported && !isPaid && (
            <View className="rounded-xl border border-amber-100 bg-amber-50 p-3">
              <ThemedText className="text-sm text-amber-800">
                <Ionicons className="text-amber-800" name="alert-circle" size={14} />{' '}
                {isProvider
                  ? 'Confira antes de aprovar.'
                  : 'O prestador foi notificado e validará seu pagamento em breve.'}
              </ThemedText>
            </View>
          )}

          {/* O que está sendo cobrado */}
          <SectionCard icon="document-text-outline" title="Esta cobrança">
            <StatRow>
              <StatTile label="Valor" value={formatCurrency(charge.amount)} emphasis />
              <StatTile label="Vencimento" value={formatDate(charge.dueDate)} />
            </StatRow>

            <SectionDivider />

            <Field
              label="Serviço"
              value={charge.serviceName ?? charge.enrollment?.service?.name ?? 'Serviço'}
            />

            {charge.description ? <Field label="Descrição" value={charge.description} /> : null}
          </SectionCard>

          {/* Linha do tempo (só cobrança automática, plano 6.6) */}
          {isAutomatic && (
            <SectionCard icon="time-outline" title="Linha do tempo">
              <ChargeTimeline charge={charge} />
            </SectionCard>
          )}

          {/* Bruto / taxa / líquido (só cobrança automática, plano 6.6) */}
          {isAutomatic && (
            <SectionCard icon="cash-outline" title="Valor do pagamento">
              <StatRow>
                <StatTile
                  label={CHARGE_AMOUNTS.grossLabel}
                  value={formatCurrency(charge.grossAmount ?? charge.amount)}
                />
                <StatTile
                  label={CHARGE_AMOUNTS.feeLabel}
                  value={
                    charge.feeAmount != null
                      ? formatCurrency(charge.feeAmount)
                      : CHARGE_AMOUNTS.pending
                  }
                />
                <StatTile
                  label={CHARGE_AMOUNTS.netLabel}
                  value={
                    charge.netAmount != null
                      ? formatCurrency(charge.netAmount)
                      : CHARGE_AMOUNTS.pending
                  }
                  emphasis
                />
              </StatRow>
              {charge.paymentMethodUsed && (
                <Field
                  label="Forma de pagamento"
                  value={paymentMethodLabel(charge.paymentMethodUsed)}
                />
              )}
            </SectionCard>
          )}

          {/* Comprovante */}
          {(charge.proofOfPaymentUrl || isReported) && (
            <SectionCard
              icon="receipt-outline"
              title="Comprovante"
              badge={charge.proofOfPaymentUrl ? 'Anexado' : 'Sem anexo'}
              badgeTone={charge.proofOfPaymentUrl ? 'success' : 'muted'}
            >
              {charge.proofOfPaymentUrl ? (
                <Button
                  variant="outline"
                  onPress={handleViewProof}
                  className="h-16 flex-row items-center justify-start gap-4 rounded-2xl border-neutral-200 bg-card px-4"
                >
                  <View className="rounded-full bg-primary/10 p-2 dark:bg-primary">
                    <Ionicons name="eye" size={22} className="text-primary dark:text-white" />
                  </View>
                  <View className="flex-1">
                    <ThemedText className="text-left font-bold text-base text-neutral-900">
                      Ver arquivo
                    </ThemedText>
                    <ThemedText className="text-left text-xs text-neutral-500">
                      Enviado em {formatDate(charge.proofOfPaymentAt!)}
                    </ThemedText>
                  </View>
                  <Ionicons name="chevron-forward" size={20} className="text-neutral-400" />
                </Button>
              ) : (
                <ThemedText className="text-sm leading-5 text-neutral-500">
                  O pagamento foi informado sem anexo — não há arquivo para conferir.
                </ThemedText>
              )}
            </SectionCard>
          )}

          {/* Como pagar (só do lado do cliente) */}
          {!isPaid && !isProvider && (
            <SectionCard icon="card-outline" title="Como pagar">
              {charge.pixCopyPaste && (
                <View className="gap-2">
                  <ThemedText className="text-xs text-neutral-500">Pix copia e cola</ThemedText>
                  <Pressable
                    onPress={handleCopyPix}
                    className="flex-row items-center gap-2 rounded-2xl border border-neutral-200 bg-neutral-50 p-3 active:bg-neutral-100"
                  >
                    <ThemedText className="flex-1 font-mono text-xs text-primary" numberOfLines={2}>
                      {charge.pixCopyPaste}
                    </ThemedText>
                    <Ionicons name="copy-outline" size={18} className="text-primary" />
                  </Pressable>
                </View>
              )}

              {charge.paymentLink && (
                <View className="gap-2">
                  <ThemedText className="text-xs text-neutral-500">Link de pagamento</ThemedText>
                  <Button
                    variant="outline"
                    onPress={() => Linking.openURL(charge.paymentLink!)}
                    className="w-full rounded-2xl border-neutral-200 bg-card"
                  >
                    <Ionicons name="link-outline" size={18} className="text-foreground" />
                    <ThemedText className="ml-2 font-medium">Abrir link</ThemedText>
                  </Button>
                </View>
              )}

              {(charge.bankName || charge.bankAccount) && (
                <View className="gap-3">
                  <SectionDivider />
                  <Field label="Banco" value={charge.bankName} emptyText="Não informado" />
                  <StatRow>
                    <StatTile label="Agência" value={charge.bankAgency || '—'} />
                    <StatTile label="Conta" value={charge.bankAccount || '—'} />
                  </StatRow>
                </View>
              )}

              {charge.paymentInstructions ? (
                <View className="rounded-2xl bg-amber-50 p-3">
                  <ThemedText className="text-xs text-amber-700">
                    Instruções do prestador
                  </ThemedText>
                  <ThemedText className="text-sm leading-5 text-amber-800">
                    {charge.paymentInstructions}
                  </ThemedText>
                </View>
              ) : null}

              {!charge.pixCopyPaste && !charge.paymentLink && !charge.bankAccount && (
                <ThemedText className="text-sm leading-5 text-neutral-500">
                  O prestador não cadastrou Pix, link nem conta bancária. Combine o pagamento direto
                  com ele e depois anexe o comprovante aqui.
                </ThemedText>
              )}
            </SectionCard>
          )}
        </View>
      </ScrollView>

      <DialogFooter className="mt-4 gap-3">
        <Button variant="ghost" onPress={onClose} className="h-12 flex-1 sm:flex-none">
          <ThemedText className="font-medium text-foreground/60">Voltar</ThemedText>
        </Button>

        {canPay && (
          <Button
            onPress={handlePayPress}
            disabled={isLoadingCheckout}
            className="h-12 flex-1 bg-primary px-6 sm:flex-none"
          >
            {isLoadingCheckout ? (
              <ActivityIndicator color="white" size="small" />
            ) : (
              <View className="flex-row items-center gap-2">
                <Ionicons
                  name={isReported ? 'document-attach-outline' : 'cloud-upload-outline'}
                  size={20}
                  color="white"
                />
                <ThemedText className="font-bold text-white">
                  {isReported ? 'Reenviar Comprovante' : 'Anexar Comprovante'}
                </ThemedText>
              </View>
            )}
          </Button>
        )}

        {openForConfirm && !canSettle && (
          <ThemedText className="self-center text-sm text-muted-foreground">
            Quem confirma o pagamento é o dono.
          </ThemedText>
        )}

        {canConfirm && (
          <Button
            onPress={() => setPendingConfirm('receipt')}
            disabled={confirmPaymentMutation.isPending}
            className="h-12 flex-1 px-6 sm:flex-none"
            testID="charge-detail-modal-approve-payment-btn"
          >
            {confirmPaymentMutation.isPending ? (
              <ActivityIndicator color="white" size="small" />
            ) : (
              <View className="flex-row items-center gap-2">
                <Ionicons name="checkmark-done" size={20} color="white" />
                <ThemedText className="font-bold text-white">
                  {CHARGE_ACTIONS.confirmReceipt}
                </ThemedText>
              </View>
            )}
          </Button>
        )}

        {canUnconfirm && (
          <Button
            variant="outline"
            onPress={handleUnconfirmPayment}
            disabled={unconfirmPaymentMutation.isPending}
            className="h-12 flex-1 border-red-500 px-6 sm:flex-none"
          >
            {unconfirmPaymentMutation.isPending ? (
              <ActivityIndicator color="#ef4444" size="small" />
            ) : (
              <View className="flex-row items-center gap-2">
                <Ionicons name="remove-circle-outline" size={20} className="text-red-500" />
                <ThemedText className="font-bold text-red-500">Desfazer Confirmação</ThemedText>
              </View>
            )}
          </Button>
        )}

        {canConvertToAutomatic && (
          <Button
            variant="outline"
            onPress={() => setPendingConfirm('convert')}
            disabled={convertToAutomaticMutation.isPending}
            className="h-12 flex-1 px-6 sm:flex-none"
          >
            {convertToAutomaticMutation.isPending ? (
              <ActivityIndicator size="small" />
            ) : (
              <View className="flex-row items-center gap-2">
                <Ionicons name="flash-outline" size={20} className="text-foreground" />
                <ThemedText className="font-bold">{CHARGE_ACTIONS.convertToAutomatic}</ThemedText>
              </View>
            )}
          </Button>
        )}

        {canRequestRefund && (
          <Button
            variant="outline"
            onPress={() => setIsRefundOpen(true)}
            className="h-12 flex-1 px-6 sm:flex-none"
          >
            <View className="flex-row items-center gap-2">
              <Ionicons name="arrow-back-outline" size={20} className="text-foreground" />
              <ThemedText className="font-bold">
                {isAutomatic ? CHARGE_ACTIONS.refund : 'Reembolsar'}
              </ThemedText>
            </View>
          </Button>
        )}
      </DialogFooter>
    </>
  );

  if (!visible) return null;

  return (
    <Dialog open={visible} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={contentClassName}>{modalBodyContent}</DialogContent>

      <Modal
        visible={isImageViewerVisible}
        transparent={false}
        animationType="fade"
        onRequestClose={() => setIsImageViewerVisible(false)}
      >
        <View className="flex-1 items-center justify-center bg-black">
          <Pressable
            onPress={() => setIsImageViewerVisible(false)}
            className="absolute right-6 top-12 z-20 rounded-full bg-black/50 p-3"
          >
            <Ionicons name="close" size={32} color="white" />
          </Pressable>

          {charge.proofOfPaymentUrl && (
            <Image
              source={{ uri: charge.proofOfPaymentUrl }}
              accessibilityLabel="Comprovante de pagamento"
              className="h-full w-full"
              resizeMode="contain"
            />
          )}

          <View className="absolute bottom-10 left-0 right-0 items-center">
            <Button
              variant="outline"
              onPress={() => setIsImageViewerVisible(false)}
              className="h-12 rounded-full border-white/20 bg-black/40 px-8"
            >
              <ThemedText className="font-bold text-white">Fechar Visualização</ThemedText>
            </Button>
          </View>
        </View>
      </Modal>

      <ConfirmDialog
        open={pendingConfirm !== null}
        onOpenChange={(open) => !open && setPendingConfirm(null)}
        title={
          pendingConfirm === 'convert'
            ? CHARGE_ACTIONS.convertToAutomaticConfirmTitle
            : CHARGE_ACTIONS.confirmReceipt
        }
        message={
          pendingConfirm === 'convert'
            ? CHARGE_ACTIONS.convertToAutomaticConfirmBody
            : isAutomatic
              ? CHARGE_ACTIONS.confirmReceiptAutomaticBody
              : CHARGE_ACTIONS.confirmReceiptBody
        }
        onConfirm={pendingConfirm === 'convert' ? handleConvertToAutomatic : handleConfirmPayment}
      />

      {canRequestRefund && (
        <RefundModal
          charge={charge}
          open={isRefundOpen}
          onOpenChange={setIsRefundOpen}
          onSuccess={() => {
            onUpdate?.();
          }}
        />
      )}
    </Dialog>
  );
}
